import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  User,
  LogOut,
  Shield,
  Building2,
  UserCircle2,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/modules/auth";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import SidebarItem from "./SidebarItem";
import { getNavigationItems } from "./navigationItems";

interface DesktopSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  organizationName?: string | null;
  organizationLogoUrl?: string | null;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  organizationName,
  organizationLogoUrl,
}) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isActivePath = (path: string) => location.pathname === path;
  const sidebarWidth = isCollapsed ? "w-16" : "w-64";

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    setIsLogoutModalOpen(false);
    setIsUserMenuOpen(false);
    logout();
  };

  const userMenuItems = useMemo(() => {
    const items = [
      { label: "Mi Perfil", path: "/profile", icon: <UserCircle2 size={16} /> },
      { label: "Seguridad", path: "/settings/2fa", icon: <Shield size={16} /> },
    ];

    if (user?.role === "ADMIN") {
      items.splice(1, 0, {
        label: "Mi Organización",
        path: "/organization",
        icon: <Building2 size={16} />,
      });
    }

    return items;
  }, [user?.role]);

  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.pathname, isCollapsed]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <aside
      className={`hidden xl:block transition-all duration-300 ${sidebarWidth} h-screen fixed left-0 top-0 z-30`}
    >
      <div className="flex flex-col w-full h-full sidebar-legal">
        {/* Logo y botón de colapsar */}
        <div className="header-legal-dark flex items-center justify-between h-16 flex-shrink-0 px-4">
          {!isCollapsed && (
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-white/95 flex items-center justify-center shadow-legal overflow-hidden flex-shrink-0">
                {organizationLogoUrl ? (
                  <img
                    src={organizationLogoUrl}
                    alt="Logo de empresa"
                    className="w-full h-full object-contain p-1"
                  />
                ) : (
                  <img
                    src="/LOGO_PLUSSIGNER.svg"
                    alt="Plussigner"
                    className="w-full h-full object-contain p-1"
                  />
                )}
              </div>
              <span
                className="text-white font-medium text-base min-w-0 truncate"
                title={organizationName || "Plussigner"}
              >
                {organizationName || "Plussigner"}
              </span>
            </div>
          )}

          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-all duration-200 flex-shrink-0"
          >
            {isCollapsed ? (
              <ChevronRight size={18} />
            ) : (
              <ChevronLeft size={18} />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto overflow-x-hidden min-h-0">
          {getNavigationItems(user?.role).map((item) => (
            <SidebarItem
              key={item.href}
              icon={item.icon}
              label={item.name}
              path={item.href}
              isActive={isActivePath(item.href)}
              onClick={() => {}}
              isCollapsed={isCollapsed}
            />
          ))}
        </nav>

        {/* User info */}
        {user && (
          <div
            ref={userMenuRef}
            className="relative flex-shrink-0 p-3 border-t border-white/10 bg-navy/50"
          >
            {isUserMenuOpen && (
              <div
                className={`absolute bottom-full mb-2 bg-navy border border-white/15 rounded-xl shadow-legal-xl p-2 z-50 ${
                  isCollapsed ? "left-14 w-56" : "left-3 right-3"
                }`}
              >
                <div className="space-y-1">
                  {userMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isActivePath(item.path)
                          ? "bg-primary/30 text-white"
                          : "text-white/85 hover:text-white hover:bg-white/10"
                      }`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-300 hover:text-red-200 hover:bg-red-500/15 transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </div>
            )}

            {!isCollapsed ? (
              <>
                <button
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  className="w-full flex items-center gap-3 px-3 py-3 mb-2 min-w-0 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <User size={16} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {user.fullName}
                    </p>
                    <p className="text-xs text-white/70 truncate">
                      {user.profile?.profileName}
                    </p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-white/70 transition-transform ${
                      isUserMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </>
            ) : (
              <div>
                <button
                  onClick={() => setIsUserMenuOpen((prev) => !prev)}
                  className="w-full p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 hover:text-white transition-all duration-200 group relative flex items-center justify-center"
                  title={user.fullName}
                >
                  <User size={20} />
                  <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-navy text-white px-3 py-2 rounded-lg text-sm opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 z-50 whitespace-nowrap shadow-legal-lg">
                    {user.fullName}
                  </div>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        title="Confirmar cierre de sesión"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setIsLogoutModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmLogout}>
              Cerrar sesión
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600">
          ¿Seguro que deseas cerrar sesión? Tendrás que iniciar sesión
          nuevamente para continuar.
        </p>
      </Modal>
    </aside>
  );
};

export default DesktopSidebar;
